import React, { useContext, useEffect, useRef, useState } from 'react';
import './DynamicFormField.scss';
import { Address, FormField, ItemTypes, VaccineFormData } from '../types';
import { default as ReactSelect, components } from 'react-select';

import { ReactComponent as DragHandleIcon } from '@/assets/img/drag-handle.svg';
import { ReactComponent as EditIcon } from '@/assets/img/edit-icon-new-v2.svg';
import { ReactComponent as DeleteIcon } from '@/assets/img/delete-icon-v2.svg';
import { ReactComponent as CalendarIcon } from '@/assets/img/calendar-icon-v2-grey.svg';

import { ActionMenu } from '@/components/Elements/ActionMenu';
import DatePicker, { DayValue } from 'react-modern-calendar-datepicker';

import { useDrag, useDrop } from 'react-dnd';
import moment from 'moment';
import AddressFieldComponent from './AddressFieldComponent';
import PhoneInput from 'react-phone-input-2';
import { PetFieldsComponent } from './PetFieldsComponent';
import { PetLimited } from '@/features/customers';
import { VaccinationFieldComponent } from './VaccinationFieldComponent';
import { RadioButtonFieldComponent } from './RadioButtonFieldComponent';
import { Insurance, InsuranceFieldComponent } from './InsuranceFieldComponent';
import { UserContext } from '@/routes';
import { useTranslation } from 'react-i18next';
import { CheckBoxFieldComponent } from './CheckBoxFieldComponent';
import DatePickerWithPortal from '@/components/DatePicker/DatepickerWithPortal';
import { LinkFieldComponent } from './LinkFieldComponent';
import Utils from '@/utils/util';
type DynamicFormFieldProps = {
  id?: any;
  data: FormField;
  index?: number;
  disableDrag?: boolean;
  moveCard?: (id: string, to: number) => void;
  findCard?: (id: string) => { index: number };
  disabled?: boolean;
  onDeleteField?: () => void;
  onEditField?: () => void;
  onFieldValueChange?: (formField: FormField) => void;
  editable?: boolean;
};
interface DragItem {
  index: number;
  id: string;
  type: string;
}

const style = {
  backgroundColor: 'white',
  cursor: 'move',
};

const DynamicFormField = ({
  id,
  data,
  index = -1,
  disableDrag,
  moveCard,
  disabled,
  findCard,
  onEditField,
  onDeleteField,
  editable,
  onFieldValueChange,
}: DynamicFormFieldProps) => {
  const userContext = useContext(UserContext);
  const [formFieldData, setFormFieldData] = useState<FormField>();
  useEffect(() => {
    setFormFieldData(data);
    //console.log('re rnder ---> ', formFieldData?.fieldType, formFieldData?.fieldValue);
  }, [data]);

  const originalIndex = findCard ? findCard(id).index : -1;

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ItemTypes.CARD,
      item: { id, originalIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        const { id: droppedId, originalIndex } = item;
        const didDrop = monitor.didDrop();
        if (!didDrop) {
          moveCard && moveCard(droppedId, originalIndex);
        }
      },
    }),
    [id, originalIndex, moveCard]
  );

  const opacity = isDragging ? 1 : 1;
  const onInputChange = (e: any) => {
    if (formFieldData && !disabled) {
      formFieldData.fieldValue = e.target.value;
      removeErrorClass();
      onFieldValueChange && onFieldValueChange(formFieldData);
    }
  };
  const onFormFieldValueChange = (
    value: string | Address | PetLimited | VaccineFormData | Insurance | string[]
  ) => {
    if (formFieldData) {
      formFieldData.fieldValue = value;
      removeErrorClass();
      onFieldValueChange && onFieldValueChange(formFieldData);
    }
  };
  const onPhoneFieldChange = (
    value: string | Address | PetLimited | VaccineFormData | Insurance | string[],
    country: any
  ) => {
    if (formFieldData) {
      formFieldData.isValid = value === country.dialCode ? false : true;
      formFieldData.fieldValue = value;
      removeErrorClass();
      onFieldValueChange && onFieldValueChange(formFieldData);
    }
  };
  const removeErrorClass = () => {
    const errorField = document.getElementById(`intake-field-${formFieldData?.id}`) as HTMLElement;
    if (errorField) {
      errorField.classList.remove('petsked-error'); // Add class if input is empty
    }
  };
  const renderInput = () => {
    const elementType = formFieldData?.fieldType;
    switch (elementType) {
      case 'textinput':
      case 'email':
        return (
          <input
            disabled={disabled}
            type="text"
            placeholder={formFieldData?.placeholder ? formFieldData?.placeholder : ''}
            value={formFieldData?.fieldValue ? formFieldData?.fieldValue : ''}
            onChange={onInputChange}
            className={
              formFieldData?.fieldValue && Utils.isValidEmail(formFieldData?.fieldValue)
                ? ''
                : 'empty'
            }
          />
        );

      case 'textarea':
        return (
          <textarea
            placeholder={formFieldData?.placeholder ? formFieldData?.placeholder : ''}
            value={formFieldData?.fieldValue ? formFieldData?.fieldValue : ''}
            onChange={onInputChange}
            className={formFieldData?.fieldValue ? '' : 'empty'}
          />
        );
      case 'select':
        return (
          <ReactSelect
            classNamePrefix={'petsked-multiselect'}
            className={`multiselect-react ${formFieldData?.fieldValue ? '' : 'empty'}`}
            isMulti={false}
            options={formFieldData?.options}
            placeholder={formFieldData?.placeholder}
            components={{
              Option,
            }}
            onChange={(selected: any) => {
              onFormFieldValueChange(selected.value);
            }}
            value={
              formFieldData?.fieldValue
                ? formFieldData?.options?.find((bl) => bl.value == formFieldData?.fieldValue)
                : null
            }
            menuPlacement="auto"
            isDisabled={disabled}
          />
        );

      case 'datepicker':
        return (
          <DatePickerWithPortal
            value={formFieldData?.fieldValue}
            onChange={(val: DayValue) => {
              onFormFieldValueChange(`${val?.month}/${val?.day}/${val?.year}`);
            }}
            disabled={disabled}
            placeholder={formFieldData?.placeholder}
            validateData={true}
          />
        );
      case 'address':
        return (
          <AddressFieldComponent
            data={formFieldData?.fieldValue}
            disabled={disabled ? disabled : false}
            onFormFieldChange={onFormFieldValueChange}
          />
        );
      case 'phone':
        return (
          <div className="new-figma-phone">
            <PhoneInput
              placeholder={formFieldData?.placeholder}
              value={formFieldData?.fieldValue ? formFieldData?.fieldValue : ''}
              onChange={onPhoneFieldChange}
              country={'us'}
              countryCodeEditable={false}
              inputClass={
                formFieldData?.fieldValue ? (formFieldData?.isValid ? '' : 'empty') : 'empty'
              }
              containerClass={
                formFieldData?.fieldValue
                  ? formFieldData?.isValid
                    ? ''
                    : 'phone-empty'
                  : 'phone-empty'
              }
            />
          </div>
        );
      case 'petbasic':
        return (
          <PetFieldsComponent
            data={formFieldData?.fieldValue}
            onChange={onFormFieldValueChange}
            disabled={disabled}
          />
        );
      case 'petvaccine':
        return (
          <VaccinationFieldComponent
            data={formFieldData?.fieldValue}
            onChange={onFormFieldValueChange}
            disabled={disabled}
          />
        );
      case 'petinsurance':
        return (
          <InsuranceFieldComponent
            data={formFieldData?.fieldValue}
            onChange={onFormFieldValueChange}
            disabled={disabled}
          />
        );
      case 'radio':
        return (
          <RadioButtonFieldComponent
            data={formFieldData}
            onChange={onFormFieldValueChange}
            disabled={disabled}
          />
        );
      case 'checkbox':
        return (
          <CheckBoxFieldComponent
            data={formFieldData}
            onChange={onFormFieldValueChange}
            disabled={disabled}
          />
        );
      case 'terms_checkbox':
        return (
          <LinkFieldComponent
            data={formFieldData}
            onChange={onFormFieldValueChange}
            disabled={disabled}
          />
        );
      default:
        return <input />;
        break;
    }
  };
  const [, drop] = useDrop(
    () => ({
      accept: ItemTypes.CARD,
      hover({ id: draggedId }: any) {
        if (draggedId !== id) {
          if (findCard) {
            const { index: overIndex } = findCard(id);
            moveCard && moveCard(draggedId, overIndex);
          }
        }
      },
    }),
    [findCard, moveCard]
  );
  const { t, i18n } = useTranslation();

  return (
    <div
      ref={preview}
      id={`intake-field-${data.id}`}
      className="dynamic-field"
      style={disableDrag ? undefined : { opacity }}
    >
      {!disableDrag && (
        <div
          className="drag-icon"
          ref={disableDrag ? undefined : (node) => drag && drop && drag(drop(node))}
          style={disableDrag ? undefined : { ...style, opacity }}
        >
          <DragHandleIcon />
        </div>
      )}
      <div className="form-element">
        <label className="form-element-label">{data.fieldLabel}</label>
        <div className="input-element">{renderInput()}</div>
      </div>
      {editable && userContext?.isLandscape ? (
        <div className="form-action">
          <DeleteIcon onClick={onDeleteField} />
          {data.businessId && <EditIcon onClick={onEditField} />}
        </div>
      ) : (
        editable && (
          <ActionMenu
            options={[
              {
                label: t('Edit'),
                icon: EditIcon,
                onClickHandler: () => onEditField && onEditField(),
                itemClassName: '',
                hide: data.businessId ? false : true,
              },
              {
                label: t('Delete'),
                icon: DeleteIcon,
                onClickHandler: () => onDeleteField && onDeleteField(),
                itemClassName: '',
              },
            ]}
          />
        )
      )}
    </div>
  );
};
const Option = (props: any) => {
  return (
    <div>
      <components.Option {...props}>
        <div className="option-label">{props.label}</div>
      </components.Option>
    </div>
  );
};

export default DynamicFormField;
